// Bookings data layer — list/detail/calendar/dashboard all read through this
// module. Every query key includes `businessId` (plan L346-348 / the
// property-switcher contract in PropertyContext.tsx), so switching
// properties never serves a stale cache entry from a different business.
//
// `display_status`/`display_flags` are PostgREST computed columns
// (`booking_display_status`/`booking_display_flags`,
// supabase/migrations/20260925000007_booking_display_status.sql) — the
// portal reads the server's rendering rather than re-deriving it from raw
// columns, so it can never disagree with the SQL mirror the pgTAP suite
// checks. Money legs (fee/balance) are computed client-side from the raw
// columns via bookingMoney.ts (D5), because those aren't exposed as
// computed columns anywhere.

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '../auth/authContext';
import type { BookingDisplayFlags, BookingDisplayStatus } from '@namibiago/shared/policy';

/** Selected columns shared by the list, detail and calendar/dashboard
 * queries — one literal string (not built by concatenation: task 11's
 * ruling notes that `+`-joined select strings break supabase-js's
 * compile-time select-string type inference). */
const BOOKING_SELECT =
  'id, reference_number, business_id, listing_id, booking_type, status, cancelled_by, ' +
  'check_in, check_out, guests, total_price, currency, deposit_amount, fee_status, ' +
  'balance_status, balance_recorded_amount_nad, balance_method, fee_eur_cents, ' +
  'fx_rate_nad_per_eur, checked_in_at, no_show_at, completed_at, qr_version, notes, ' +
  'created_at, ' +
  'display_status:booking_display_status, display_flags:booking_display_flags, ' +
  'profiles!bookings_user_id_fkey ( display_name ), listings ( title )';
// (See the note above the const: this is one literal, not a concatenation.)

export interface BookingRow {
  id: string;
  reference_number: string | null;
  business_id: string;
  listing_id: string | null;
  booking_type: string;
  status: string;
  cancelled_by: string | null;
  check_in: string | null;
  check_out: string | null;
  guests: number | null;
  total_price: number | string | null;
  currency: string;
  deposit_amount: number | string | null;
  fee_status: string;
  balance_status: string;
  balance_recorded_amount_nad: number | string | null;
  balance_method: string | null;
  fee_eur_cents: number | null;
  fx_rate_nad_per_eur: number | string | null;
  checked_in_at: string | null;
  no_show_at: string | null;
  completed_at: string | null;
  qr_version: number;
  notes: string | null;
  created_at: string;
  display_status: BookingDisplayStatus | null;
  display_flags: BookingDisplayFlags | null;
  profiles: { display_name: string | null } | null;
  listings: { title: string } | null;
}

export interface BookingEventRow {
  id: string;
  booking_id: string;
  actor_type: 'guest' | 'host' | 'system' | 'admin';
  actor_user_id: string | null;
  event: string;
  from_status: string | null;
  to_status: string | null;
  amount_nad: number | string | null;
  method: string | null;
  device: string | null;
  created_at: string;
}

export interface BookingFilters {
  /** `display_status` values to include; empty/undefined means "all". */
  statuses?: BookingDisplayStatus[];
  /** Inclusive `check_in` date range, `yyyy-MM-dd`. */
  from?: string;
  to?: string;
  /** Free-text match against reference number or guest name (client-side —
   * see the module doc: PostgREST .or() across an embedded relation isn't
   * reliable, and a host's own booking list is small). */
  search?: string;
}

/** Exported so the list route can show a "showing the first N" notice when
 * a filtered result set is exactly at the cap (review round 1, item 4) —
 * the two must never drift apart silently. */
export const LIST_LIMIT = 500;

export interface BookingsListResult {
  rows: BookingRow[];
  /** True when the SERVER fetch (before the client-side status/search
   * narrowing below) came back at exactly `LIST_LIMIT` — i.e. there may be
   * more rows the date-range filter alone didn't rule out (review round 1,
   * item 4). Deliberately computed before the client-side filters run:
   * status/search narrowing the visible count back down below the cap
   * doesn't mean the underlying date-range fetch wasn't itself truncated. */
  isLimited: boolean;
}

async function fetchBookingsList(
  supabase: SupabaseClient,
  businessId: string,
  filters: BookingFilters,
): Promise<BookingsListResult> {
  let query = supabase
    .from('bookings')
    .select(BOOKING_SELECT)
    .eq('business_id', businessId)
    .neq('booking_type', 'inquiry')
    .order('check_in', { ascending: false, nullsFirst: false })
    .limit(LIST_LIMIT);

  if (filters.from) query = query.gte('check_in', filters.from);
  if (filters.to) query = query.lte('check_in', filters.to);

  const { data, error } = await query;
  if (error) throw error;
  const fetched = (data ?? []) as unknown as BookingRow[];
  const isLimited = fetched.length >= LIST_LIMIT;
  let rows = fetched;

  if (filters.statuses && filters.statuses.length > 0) {
    const wanted = new Set(filters.statuses);
    rows = rows.filter((r) => r.display_status && wanted.has(r.display_status));
  }

  if (filters.search && filters.search.trim().length > 0) {
    const needle = filters.search.trim().toLowerCase();
    rows = rows.filter(
      (r) =>
        (r.reference_number ?? '').toLowerCase().includes(needle) ||
        (r.profiles?.display_name ?? '').toLowerCase().includes(needle),
    );
  }

  return { rows, isLimited };
}

export function useBookingsList(businessId: string | null, filters: BookingFilters) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['bookings', businessId, filters],
    queryFn: () => fetchBookingsList(supabase, businessId!, filters),
    enabled: !!businessId,
    staleTime: 15_000,
  });
}

/** Every booking with a `check_in` in `[from, to]` — the shared fetch behind
 * the calendar grid, the dashboard's "today's arrivals" and the arrivals
 * print route. Unlike `useBookingsList`, this does NOT exclude inquiries by
 * itself — none of those three consumers deal in inquiries anyway (they all
 * have a `check_in`), so the extra filter would be a no-op; kept simple. */
async function fetchBookingsInRange(
  supabase: SupabaseClient,
  businessId: string,
  from: string,
  to: string,
): Promise<BookingRow[]> {
  const { data, error } = await supabase
    .from('bookings')
    .select(BOOKING_SELECT)
    .eq('business_id', businessId)
    .gte('check_in', from)
    .lte('check_in', to)
    .order('check_in', { ascending: true })
    .limit(LIST_LIMIT);
  if (error) throw error;
  return (data ?? []) as unknown as BookingRow[];
}

export function useBookingsInRange(businessId: string | null, from: string, to: string) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['bookings-range', businessId, from, to],
    queryFn: () => fetchBookingsInRange(supabase, businessId!, from, to),
    enabled: !!businessId,
    staleTime: 15_000,
  });
}

/** Balance-leg statuses that still owe the host money — the complement of
 * `not_due`/`received` (D5's "nothing left to collect" set). */
const OUTSTANDING_BALANCE_STATUSES = ['due', 'reported', 'partial', 'not_recorded'];
/** Terminal non-stays: a balance was never truly "due" from a guest who
 * never actually stayed, so these never belong in an outstanding list even
 * if `balance_status` lags (review round 1, dashboard item 3). */
const TERMINAL_NON_STAY_STATUSES = '(cancelled,rejected,expired)';

/** Every booking across ALL time with an outstanding balance — the
 * dashboard's "Outstanding balances" tile (review round 1, item 3: the
 * month-scoped `bookings-range` query was silently hiding a balance from,
 * say, a booking that checked in last month and still hasn't paid). A
 * dedicated, narrow server-side filter rather than widening the range
 * query, since "everything, forever" is a different shape of query from
 * "this month" and mixing the two would make the month-scoped stats wrong
 * instead. */
async function fetchOutstandingBalances(supabase: SupabaseClient, businessId: string): Promise<BookingRow[]> {
  const { data, error } = await supabase
    .from('bookings')
    .select(BOOKING_SELECT)
    .eq('business_id', businessId)
    .in('balance_status', OUTSTANDING_BALANCE_STATUSES)
    .is('no_show_at', null)
    .not('status', 'in', TERMINAL_NON_STAY_STATUSES)
    .order('check_in', { ascending: true, nullsFirst: false })
    .limit(LIST_LIMIT);
  if (error) throw error;
  return (data ?? []) as unknown as BookingRow[];
}

export function useOutstandingBalances(businessId: string | null) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['bookings-outstanding', businessId],
    queryFn: () => fetchOutstandingBalances(supabase, businessId!),
    enabled: !!businessId,
    staleTime: 15_000,
  });
}

async function fetchBookingByReference(
  supabase: SupabaseClient,
  businessId: string,
  reference: string,
): Promise<BookingRow | null> {
  const { data, error } = await supabase
    .from('bookings')
    .select(BOOKING_SELECT)
    .eq('business_id', businessId)
    .eq('reference_number', reference)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as BookingRow | null;
}

export function useBookingByReference(businessId: string | null, reference: string | undefined) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['booking', businessId, reference],
    queryFn: () => fetchBookingByReference(supabase, businessId!, reference!),
    enabled: !!businessId && !!reference,
    staleTime: 15_000,
  });
}

async function fetchBookingEvents(supabase: SupabaseClient, bookingId: string): Promise<BookingEventRow[]> {
  const { data, error } = await supabase
    .from('booking_events')
    .select('id, booking_id, actor_type, actor_user_id, event, from_status, to_status, amount_nad, method, device, created_at')
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as BookingEventRow[];
}

export function useBookingEvents(bookingId: string | undefined) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['booking-events', bookingId],
    queryFn: () => fetchBookingEvents(supabase, bookingId!),
    enabled: !!bookingId,
    staleTime: 15_000,
  });
}

export function guestName(row: Pick<BookingRow, 'profiles'>): string {
  return row.profiles?.display_name?.trim() || 'Guest';
}

export function listingTitle(row: Pick<BookingRow, 'listings'>): string {
  return row.listings?.title ?? '—';
}
